<template>
  <fragment v-if="campaign">
    <div class="channel-edit-group channel-edit-group-fields">
      <div class="field-w">
        <b-field :label="$t('CampaignEdit.Cost.CostType')">
          <b-select expanded :placeholder="$t('CampaignEdit.Cost.CostType')" v-model="campaign.CostTypeID">
            <option v-for="costType in campaignEditCostsData.CostType" :key="+costType.Value" :value="+costType.Value">
              {{ costType.Text }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="field-w">
        <b-field :label="$t('CampaignEdit.Cost.CostAmount')">
          <b-input type="number" v-model="campaign.CostAmount"
                   :placeholder="$t('CampaignEdit.Cost.CostAmount')"></b-input>
        </b-field>
      </div>
      <div class="field-w" v-if="campaign.CostTypeID === ChannelCostType.LeadPrice">
        <b-field :label="$t('CampaignEdit.Cost.Status')">
          <b-dropdown v-model="campaign.CostStatusesList" multiple scrollable aria-role="list" class="multiple"
                      @active-change="onDropdownActiveChange" @change="onSelectedStatusesChange($event)">
            <template #trigger>
              <b-button>
                <span class="dropdown-trigger-value">{{
                    $t("CampaignEdit.Cost.StatusPlaceholder")
                  }} ({{ campaign.CostStatusesList.length }})</span>
                <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down">
                  <icon-caret-down/>
                </icon-base>
              </b-button>
            </template>
            <b-dropdown-item custom aria-role="listitem" class="filter-search">
              <b-input v-model="statusSearch" class="search" :placeholder="$t('Search')" expanded/>
            </b-dropdown-item>
            <b-dropdown-item v-for="status in statusesFiltered" :key="+status.StatusId" :value="+status.StatusId"
                             aria-role="listitem">
              <span class="text">{{ status.StatusName }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
    </div>
    <div class="grid-content" ref="tagsTableContainer" v-if="campaign.CostTypeID === ChannelCostType.LeadPrice">
      <b-table :data="tags ? tags.Data : []" hoverable sticky-header
               :total="tags ? tags.Total : 0"
               class="tags-table"
               ref="tags">
        <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class"
                        :label="column.label"
                        :visible="column.visible && !column.forbidden && column.id < 1000"
                        :width="column.width"
                        :sortable="column.sortable"
                        :header-class="'column-th-' + column.name">
          <template v-slot="props">
            <div :style="getColumnStyle(column)">
              {{ props.row[column.name] }}
            </div>
          </template>
        </b-table-column>
        <b-table-column cell-class="cell-actions" field="actions" :label="$t('Leads.Grid.Columns.Actions')" width="80"
                        :sticky="true" v-slot="props"
                        :visible="isActionsColumnVisible">
          <div class="actions-list">
            <b-tooltip :label="$t('Leads.Grid.ActionsColumn.Edit')" position="is-top" type="is-dark">
              <div class="action" @click="openEditTagDialog($event, props.row)">
                <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="pencil">
                  <icon-pencil/>
                </icon-base>
              </div>
            </b-tooltip>
          </div>
        </b-table-column>
      </b-table>
    </div>
  </fragment>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import Fragment from "vue-fragment";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import {Getter} from "vuex-class";
import i18n from "@/i18n";
import {DataSourceResult} from "@/models/DataSourceResult";
import {GridColumn} from "@/models/GridColumn";
import {Campaign, CampaignEditViewModel} from "@/models/campaigns/Campaign";
import {CampaignTag} from "@/models/campaigns/CampaignTag";
import {CampaignEditCostsData} from "@/models/campaigns/CampaignEditCostsData";
import {CampaignEditPageKind} from "@/models/campaigns/CampaignEditPageKind";
import {ChannelCostType} from "@/models/campaigns/ChannelCostType";
import {CRMStatus} from "@/models/crm/CRMStatus";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import CampaignTagEdit from "./CampaignTagEdit.vue";
import IconBase from "../IconBase.vue";
import IconCalendar from "../icons/IconCalendar.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconPencil from "../icons/IconPencil.vue";

Vue.use(Fragment.Plugin);
const namespace = "campaignsModule";

@Component({
  components: {
    DatePicker,
    IconBase,
    IconCalendar,
    IconCaretDown,
    IconPencil,
  },
})
export default class TabCost extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", {namespace}) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  statusSearch = "";
  campaignEditCostsData?: CampaignEditCostsData = {
    CostType: [],
    StatusListData: []
  };
  tags?: DataSourceResult = {};

  columns: GridColumn[] = [
    {
      id: 1,
      name: "Tag",
      label: i18n.t("CampaignEdit.Cost.TabsGrid.Columns.Tag").toString(),
      class: "cell-project-tag",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 200
    },
    {
      id: 2,
      name: "Cost",
      label: i18n.t("CampaignEdit.Cost.TabsGrid.Columns.Cost").toString(),
      class: "cell-project-cost",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 200
    },
    {
      id: 1000,
      name: "Actions",
      label: "",
      class: "cell-project-actions",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 200
    },
  ];

  CampaignEditPageKind = CampaignEditPageKind;
  ChannelCostType = ChannelCostType;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", {immediate: true})
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    CampaignsApiService.getCampaignEditCostsData(this.campaign).then((data: CampaignEditCostsData) => {
      this.campaignEditCostsData = data;
    });
    this.refreshTags();
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  get pageKind(): CampaignEditPageKind {
    return CampaignService.getCampaignEditPageKind(this.$route.path);
  }

  get statusesFiltered(): CRMStatus[] {
    if (this.campaignEditCostsData) {
      return this.campaignEditCostsData?.StatusListData.filter((i: CRMStatus) => i.StatusName.toLowerCase().indexOf(this.statusSearch.toLowerCase()) >= 0);
    }
    return [];
  }

  //#endregion Getters


  //#region Handlers

  onDropdownActiveChange(show: boolean) {
    HelperService.onDropdownActiveChange(show);
  }

  onSelectedStatusesChange(selectedStatuses: number[]) {
    this.campaign.CostStatuses = selectedStatuses.join(",");
  }

  openEditTagDialog(e: Event, tag: CampaignTag): void {
    e.preventDefault();
    this.$buefy.modal.open({
      parent: this,
      component: CampaignTagEdit,
      hasModalCard: true,
      trapFocus: true,
      props: {
        "campaignTag": JSON.parse(JSON.stringify(tag))
      },
      events: {
        'update': (updatedTag: CampaignTag) => {
          tag.Cost = updatedTag.Cost;
          const loader = this.$loading.show({
            container: this.$refs.tagsTableContainer
          });
          CampaignsApiService.updateTagForCampaign(updatedTag).then((data: DataSourceResult) => {
            loader.hide();
          });
        }
      }
    });
  }

  //#endregion Handlers


  //#region Private methods

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  refreshTags() {
    const loader = this.$loading.show({
      container: this.$refs.tagsTableContainer
    });
    CampaignsApiService.getTagsForCampaign(this.campaign.ID).then((data: DataSourceResult) => {
      this.tags = data;
    }).finally(() => {
      loader.hide();
    });
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.channel-edit-group {
  margin-bottom: 25px;

  &.channel-edit-group-field {
    max-width: 894px;

    .channel-edit-field {
      margin-bottom: 1rem;
    }

    .label {
      font-size: 17px;
      margin: 0;
      display: inline;
    }
  }

  &.channel-edit-group-fields {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;

    .field-w {
      padding: 0 12px 25px;
      flex-grow: 1;
      width: 50%;

      @media (max-width: $bp_md) {
        padding-bottom: 1rem;
        width: 100%;
      }

      .dropdown {
        width: 100%;

        .dropdown-item-with-icon {
          display: flex;

          .icon-w {
            display: flex;
            margin-inline-end: 0.5rem;
            min-width: 22px;

            img {
              max-height: 22px;
              min-width: 22px;
              width: 22px;
            }
          }
        }

        .dropdown-trigger {

          .button {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
